import { Collapse, Grid, IconButton, Typography } from '@mui/material';
import { isPerUnitProductVariant, isTieredSubscriptionProductVariant, isTiersProductVariant } from '@weft/services';
import type { BillingInterval, Currency, UsageProductInput } from '@weft/types';
import { isNotNullOrUndefined, withDefault } from '@weft/utils';
import { formatNumber } from '@weft/utils';
import cn from 'classnames';
import React from 'react';
import { Price } from '../../../../../../../components/design/Price';
import { ArrowIcon } from '../../../../../../../components/icons/ArrowIcon';
import { ARROW_ICON_DIRECTIONS } from '../../../../../../../components/icons/ArrowIcon/types/arrow-icon-direction';
import { useToggle } from '../../../../../../../hooks/use-toggle';
import { getUsageProductInputEstimation } from '../../utils/live-preview-estimation';
import { LivePreviewAddonsTable } from '../LivePreviewAddonsTable';
import { LivePreviewPerUnitItem } from './usage-product-item-components/LivePreviewPerUnitItem';
import { LivePreviewTiersTable } from './usage-product-item-components/LivePreviewTiersTable';

type UsageProductItemProps = {
  product: UsageProductInput;
  billingInterval: BillingInterval;
  currency: Currency;
};

export const UsageProductItem: React.FC<UsageProductItemProps> = ({ product, billingInterval, currency }) => {
  const [isCollapsed, toggleCollapse] = useToggle(true);

  return (
    <>
      <div className="flex gap-x-2">
        <IconButton
          className="group h-6 w-6 rounded-md bg-[#F0F4F6]"
          onClick={() => toggleCollapse()}
        >
          <ArrowIcon
            className={cn('transition', { 'rotate-180': !isCollapsed })}
            direction={ARROW_ICON_DIRECTIONS.DOWN}
          />
        </IconButton>

        <Grid
          container
        >
          <Grid
            item
            xs={6}
          >
            <Typography
              color="#111B1E"
              fontWeight={400}
              fontSize="12px"
              lineHeight="16px"
            >
              {getName(product)}
            </Typography>
          </Grid>
          <Grid
            item
            xs={6}
            textAlign="right"
          >
            <Typography
              color="#9DA8AC"
              fontWeight={300}
              fontSize="12px"
              lineHeight="16px"
            >
              Amount
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            textAlign="right"
          >
            <Price
              color="#111B1E"
              fontWeight={400}
              fontSize="12px"
              lineHeight="16px"
              currency={currency}
              align="right"
            >
              {getUsageProductInputEstimation(product, billingInterval)}
            </Price>
          </Grid>
        </Grid>
      </div>

      <Collapse
        in={!isCollapsed}
        className="pt-1"
      >
        <div className="pb-3 pl-8 pt-2">
          <LivePreviewAddonsTable
            rows={getAddonsTableRows(product, currency)}
          />
        </div>

        <ul>
          {product.variants.map(productVariant => {
            if (isTiersProductVariant(productVariant) || isTieredSubscriptionProductVariant(productVariant)) {
              return (
                <li
                  key={productVariant.id}
                  className="space-y-2"
                >
                  <Typography
                    color="#111B1E"
                    fontWeight={400}
                    fontSize="12px"
                    lineHeight="16px"
                    marginTop="4px"
                  >
                    {productVariant.name}
                  </Typography>

                  <LivePreviewTiersTable
                    productVariant={productVariant}
                    currency={currency}
                    isRevShare={(product as UsageProductInput).isRevShare}
                  />
                </li>
              );
            }

            if (isPerUnitProductVariant(productVariant)) {
              return (
                <li
                  key={productVariant.id}
                  className="mb-2"
                >
                  <LivePreviewPerUnitItem
                    productVariant={productVariant}
                    currency={currency}
                  />
                </li>
              );
            }

            return null;
          })}
        </ul>
      </Collapse>
    </>
  );
};

function getName(product: UsageProductInput) {
  return withDefault(product.name, product.variants[0].name ?? '');
}

function getAddonsTableRows(product: UsageProductInput, currency: Currency): [React.ReactNode, React.ReactNode, ...React.ReactNode[]][] {
  const addons: [React.ReactNode, React.ReactNode, ...React.ReactNode[]][] = [];

  if (isNotNullOrUndefined(product.basePrice)) {
    addons.push([
      'Base Amount',
      <Price
        currency={currency}
        align="right"
        className="px-0 text-[10px] font-light leading-3 text-[#606A6E]"
      >
        {product.basePrice}
      </Price>,
    ]);
  }

  if (isNotNullOrUndefined(product.commitment)) {
    addons.push([
      'Commitment', `${formatNumber(product.commitment.amount)} Units`,
    ]);
  }

  if (isNotNullOrUndefined(product.rateMultiplier)) {
    addons.push([
      'Rate Multiplier', `${formatNumber(product.rateMultiplier)}x`,
    ]);
  }

  return addons;
}